/**
 * The top of every teacher screen: a way back, on the same line as the title.
 *
 * **The back link used to sit above the heading as a small underlined word**, which put a stray line
 * of text between the top of the screen and the thing the screen is about, and made every page start
 * with an apology. On the same row it reads as part of the title — the arrow says where you are, the
 * words say what you are looking at — and the page begins with its own heading.
 *
 * **Nothing here is underlined.** An underline in a page of Persian text is a heavier mark than it is
 * in Latin: it cuts the descenders and the connecting strokes, and on a screen where most controls are
 * already buttons it adds noise without adding information. Position, colour and an arrow are enough
 * to say "this goes back".
 */
export function ScreenHeader({
  title,
  subtitle,
  onBack,
  backLabel,
  action,
}: {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  /** Read by a screen reader. The arrow alone is not a label. */
  backLabel?: string;
  /** One control at the far end of the row — "add", usually. */
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-lg flex items-start gap-sm">
      {onBack && (
        <button
          onClick={onBack}
          aria-label={backLabel ?? ""}
          /**
           * A drawn circle, not a bare arrow.
           *
           * An arrow alone on a cream page is a grey mark that reads as decoration — it was there and
           * nobody could see it. A card-coloured disc with an edge and the same lift the cards have
           * makes it a thing to press, and puts it in the same visual family as everything else on the
           * screen rather than inventing a new one.
           */
          className="-mt-1 grid size-11 shrink-0 place-items-center rounded-pill border border-edge bg-card text-ink shadow-card transition-colors active:bg-cream"
        >
          <BackArrow />
        </button>
      )}

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-hero">{title}</h1>
        {subtitle && <p className="mt-1 text-small text-muted">{subtitle}</p>}
      </div>

      {action}
    </header>
  );
}

/**
 * An SVG, not a character.
 *
 * `›` is in the Unicode bidi-mirroring set, so inside an RTL element the browser flips it and the
 * glyph points the opposite way from the one written. An SVG has no bidi behaviour, so it points where
 * it is drawn — right, because the button sits at the inline start, which in RTL is the right edge.
 */
function BackArrow() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}
